import { useCallback, useMemo, useState } from "react";
import usePersistentObject from "./usePersistentObject";
import {
  ButtonProps,
  Icons,
  isDeepEqual,
  isEmpty,
} from "@pushpress/shared-components";

export interface UseFilterOptions<T> {
  storageKey: string;
  defaultValue: T;
}

const LOADING_TIMEOUT = 600;

export default function useFilter<T extends object>(
  options: UseFilterOptions<T>,
) {
  const { defaultValue, storageKey } = options;

  const [savedFilter, setSavedFilter] = usePersistentObject<T>({
    storageKey,
    defaultValue,
  });

  const [filter, setFilter] = useState<T>(savedFilter || defaultValue);

  const [loading, setLoading] = useState(false);

  const isFilterEmpty = useMemo(() => {
    return isEmpty(filter);
  }, [filter]);

  const isSavedFilterEmpty = useMemo(() => {
    return isEmpty(savedFilter);
  }, [savedFilter]);

  const isFilterSaved = useMemo(() => {
    if (isFilterEmpty) {
      return false;
    }
    return isDeepEqual(filter, savedFilter);
  }, [filter, savedFilter, isFilterEmpty]);

  const handleSaveFilter = useCallback(() => {
    setLoading(true);
    setTimeout(() => {
      const isSaved = isDeepEqual(filter, savedFilter);
      if (isSaved) {
        setSavedFilter(defaultValue);
      } else {
        setSavedFilter(filter);
      }
      setLoading(false);
    }, LOADING_TIMEOUT);
  }, [filter, savedFilter, defaultValue, setSavedFilter]);

  const filterButtonProps = {
    text: isFilterSaved ? "Saved" : "Save filters",
    buttonType: isFilterSaved ? "primary" : "secondary",
    icon: isFilterSaved ? Icons.StarFilled : Icons.Star,
    iconLocation: "left",
    disabled: isFilterEmpty && isSavedFilterEmpty,
    onClick: handleSaveFilter,
    loading,
  } as ButtonProps<"button">;

  return {
    filter,
    setFilter,
    filterButtonProps,
  };
}
